import { FC, useContext, useEffect, useMemo, useState } from 'react';

import {
  BucketIcon,
  Button,
  Checkbox,
  ContextMenu,
  Table,
  useContextMenu,
} from '@smartshell/ui-kit';
import cn from 'classnames';

import { AuthContext } from 'auth';
import { LayoutContext } from 'components/Layout';
import { DEFAULT_GROUP_COLOR } from 'consts';
import { CtxMenuHost, useDoubleClick, useShellVersion } from 'hooks';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { getHostStatus } from 'utils';

import { getColumns } from './columns';
import s from './ComputersTable.module.scss';
import { DEFAULT_SORT } from './constants';
import { ComputersTableProps } from './interfaces';
import { getRowId } from './utils';

export const ComputersTable: FC<ComputersTableProps> = ({
  group,
  hosts,
  openShopModal,
  openClientModal,
  openHostGroupEditor,
  deleteHostGroup,
  onRowClick,
  useControlledState,
  contextMenuItems,
  setSelectedHostsIds,
  selectedHostsIds,
}) => {
  const { t } = useContext(LocalizationContext);
  const layoutElement = useContext(LayoutContext);

  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [isCheckedAll, setIsCheckedAll] = useState(false);

  const {
    contextMenuData,
    closeContextMenu,
    cursorPosition,
    onContextMenu,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
  } = useContextMenu<CtxMenuHost>();
  const shellVersion = useShellVersion();

  const columns = useMemo(
    () => getColumns(openClientModal, t, shellVersion),
    [openClientModal, t, permissions, shellVersion]
  );

  const clickFunc = useDoubleClick(
    (event, id, rowIndex, flatRows) =>
      onRowClick(event, id, rowIndex, flatRows),

    (id) => {
      const currentHost = hosts.find((host) => host.id === id);
      if (!permissions?.has('payment_create')) {
        return notification.info({
          t,
          title: t('permission.error'),
          type: 'warning',
          dismiss: 5,
        });
      }

      if (currentHost && currentHost.client_sessions[0]?.postpaid) {
        return notification.info({
          t,
          title: 'Нельзя открыть магазин на данном ПК',
          type: 'warning',
          dismiss: 5,
        });
      }
      openShopModal(id);
    }
  );

  useEffect(() => {
    if (!hosts.length) {
      setIsCheckedAll(false);
      return;
    }

    const selectedInGroup = selectedHostsIds.filter((id) =>
      hosts.some((host) => host.id === id)
    );

    setIsCheckedAll(selectedInGroup.length === hosts.length);
  }, [selectedHostsIds, hosts]);

  return (
    <div className={s.root} data-onboarding="hosts-table">
      {contextMenuData && cursorPosition && (
        <ContextMenu
          layoutElement={layoutElement}
          data={contextMenuData}
          items={contextMenuItems}
          closeContextMenu={closeContextMenu}
          position={cursorPosition}
        />
      )}
      <Table
        dataTestId={`host-group-${group.id}`}
        headerBlock={
          <div className={s.header}>
            <div className={cn(s.title, hosts.length <= 1 && s.big_width)}>
              <div
                style={{
                  backgroundColor:
                    group.customization?.color || DEFAULT_GROUP_COLOR,
                }}
                className={s.color}
              />
              <span>{group.title}</span>
            </div>
            {hosts.length > 1 && (
              <div className={s.change_all}>
                <Checkbox
                  value={isCheckedAll}
                  onChange={() => {
                    const hostsIds = hosts.map((host) => host.id);

                    if (!isCheckedAll) {
                      setSelectedHostsIds((prev) => [
                        ...new Set([...prev, ...hostsIds]),
                      ]);
                    } else {
                      setSelectedHostsIds((prev) =>
                        prev.filter((id) => !hostsIds.includes(id))
                      );
                    }

                    setIsCheckedAll(!isCheckedAll);
                  }}
                  label={t('change.all')}
                />
              </div>
            )}
            {permissions?.has('host_manage') && (
              <div className={s['manager-buttons']}>
                <Button
                  variant="ghost"
                  onClick={() => {
                    openHostGroupEditor(group.id);
                  }}
                  iconName="GearIcon"
                  className={s.setting}
                />

                <div title={t('hosts.group.delete.help')}>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      if (hosts.length > 0) {
                        notification.info({
                          t,
                          type: 'warning',
                          title: t('hosts.group.delete.notify.title'),
                          message: t('hosts.group.delete.notify.message'),
                          dismiss: 3,
                        });

                        return;
                      }

                      if (deleteHostGroup) {
                        deleteHostGroup({
                          variables: { id: group.id },
                        }).catch(console.error);
                      }
                    }}
                  >
                    <BucketIcon size="m" color="error" />
                  </Button>
                </div>
              </div>
            )}
          </div>
        }
        getRowClassName={(host, selected) => {
          if (host.locked) {
            return s.locked;
          }

          const status = getHostStatus(host);

          if (status === 'BUSY' && !host.online) {
            return cn(s['with-problems'], selected && s.selected);
          }
        }}
        emptyDataPlaceholder={t('hosts.placeholder')}
        initialState={{ groupId: group.id }}
        data={hosts}
        columns={columns}
        sortBy={DEFAULT_SORT}
        onContextMenu={onContextMenu}
        onTouchStart={(event, data) => {
          setSelectedHostsIds([data.id]);
          onTouchStart(event, data);
        }}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        useControlledState={useControlledState}
        onRowClick={(e, row, table) => {
          const displayIndex = table.flatRows.findIndex((r) => r.id === row.id);

          clickFunc(e, row.original.id, displayIndex, table.flatRows);
        }}
        getRowId={getRowId}
        className={s.table}
      />
    </div>
  );
};
